import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, BrutalCard, StatCard } from "../components/brutal";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
} from "recharts";
import { TrendingUp, Users, CheckCircle2, PiggyBank, Download, Share2, Instagram, Activity, Link2, MessageCircle, Linkedin, Facebook, Mail as MailIcon, QrCode as QrIcon, AlertTriangle, CalendarClock, PartyPopper } from "lucide-react";
import { API } from "../lib/api";

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [engagementScore, setEngagementScore] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [sharingPeriod, setSharingPeriod] = useState("month");
  const [stale, setStale] = useState(null);
  const [staleDays, setStaleDays] = useState(14);
  const [yirBusy, setYirBusy] = useState(false);

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setData(r.data));
    api.get("/spotlight/analytics").then((r) => setEngagement(r.data)).catch(() => {});
    api.get("/analytics/engagement").then((r) => setEngagementScore(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/analytics/sharing?period=${sharingPeriod}`).then((r) => setSharing(r.data)).catch(() => {});
  }, [sharingPeriod]);

  useEffect(() => {
    api.get(`/analytics/stale-referrals?days=${staleDays}`).then((r) => setStale(r.data)).catch(() => {});
  }, [staleDays]);

  const sendYearInReview = async (dryRun) => {
    if (!dryRun && !window.confirm("Send a personalised year-in-review email to every active employee with activity this year? This is irreversible — they'll all receive the email within seconds.")) return;
    setYirBusy(true);
    try {
      const { data: res } = await api.post(`/admin/year-in-review?dry_run=${dryRun}`);
      if (dryRun) {
        toast(`Preview: ${res.eligible_count} employee(s) would receive the wrap-up email.`, { icon: "ℹ️", duration: 6000 });
      } else {
        toast.success(`🎬 Year-in-review sent to ${res.sent_count} employee(s)`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setYirBusy(false);
    }
  };

  if (!data) return <div className="p-10">Loading…</div>;

  const exportCSV = async () => {
    const token = localStorage.getItem("rp_token");
    const resp = await fetch(`${API}/reports/export/referrals.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "referrals.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Referral performance across the company."
        right={
          <button onClick={exportCSV} className="btn-brutal btn-primary" data-testid="export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
      />

      <div className="bento-grid mb-6">
        <StatCard testid="stat-total-refs" label="Total referrals" value={data.total_referrals} color="bg-white" icon={<Users className="w-6 h-6" />} />
        <StatCard testid="stat-hires" label="Hires from referrals" value={data.total_hires} color="bg-brand-green" icon={<CheckCircle2 className="w-6 h-6" />} />
        <StatCard testid="stat-conv" label="Conversion rate" value={`${data.conversion_rate}%`} color="bg-brand-yellow" icon={<TrendingUp className="w-6 h-6" />} />
        <StatCard testid="stat-saved" label="Cost saved vs. agencies" value={`€${data.cost_saved_eur.toLocaleString()}`} color="bg-brand-orange text-white" icon={<PiggyBank className="w-6 h-6" />} />
      </div>

      {engagementScore && (
        <BrutalCard className="p-6 mb-6" data-testid="engagement-score-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 bg-brand-blue text-white border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal flex-shrink-0">
              <Activity className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Employee engagement this month</div>
              <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                <div className="font-display font-black text-5xl leading-none" data-testid="engagement-percent">{engagementScore.score}%</div>
                <div className="text-sm text-neutral-700">
                  <strong>{engagementScore.engaged}</strong> of <strong>{engagementScore.total_active}</strong> active employees did something this month
                  <span className="text-xs text-neutral-500"> (referred, earned points, or reacted to a shout-out)</span>
                </div>
              </div>
              {/* progress bar */}
              <div className="mt-3 h-3 border-2 border-black rounded-full overflow-hidden bg-white">
                <div
                  className="h-full"
                  style={{ width: `${engagementScore.score}%`, background: "linear-gradient(90deg, #00E676, #0047FF)" }}
                />
              </div>
            </div>
          </div>
          {(engagementScore.rows || []).length > 0 && (
            <details className="mt-4" data-testid="engagement-breakdown">
              <summary className="cursor-pointer text-xs uppercase tracking-widest font-bold text-neutral-500 select-none">
                Who's engaged? ({(engagementScore.rows || []).filter((r) => r.engaged).length} people)
              </summary>
              <div className="mt-3 flex flex-wrap gap-2">
                {(engagementScore.rows || []).map((r) => (
                  <span
                    key={r.user_id}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 border-2 border-black rounded-full font-semibold ${r.engaged ? "bg-brand-green" : "bg-neutral-200 text-neutral-500"}`}
                    title={`${r.name} · ${r.engaged ? "engaged this month" : "not engaged yet"}`}
                  >
                    {r.name}
                  </span>
                ))}
              </div>
            </details>
          )}
        </BrutalCard>
      )}

      {stale && (
        <BrutalCard className="p-5 mb-6" data-testid="stale-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${stale.count > 0 ? "bg-red-500" : "bg-brand-green"} text-white border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal`}>
                <CalendarClock className="w-6 h-6" strokeWidth={2.4} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Stale referrals</div>
                <h3 className="font-display font-black text-xl leading-tight">
                  {stale.count === 0 ? "Pipeline is fresh — no stale referrals 🎉" : `${stale.count} referral${stale.count === 1 ? "" : "s"} sitting more than ${stale.days_threshold} days`}
                </h3>
              </div>
            </div>
            <div className="brutal p-1 inline-flex gap-1" data-testid="stale-tabs">
              {[7, 14, 30, 60].map((d) => (
                <button key={d} onClick={() => setStaleDays(d)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${staleDays === d ? "bg-brand-yellow" : "hover:bg-neutral-100"}`} data-testid={`stale-${d}d`}>
                  {d}d+
                </button>
              ))}
            </div>
          </div>
          {stale.count > 0 && (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm" data-testid="stale-table">
                <thead className="bg-brand-ink text-white">
                  <tr>
                    <th className="text-left p-2">Candidate</th>
                    <th className="text-left p-2 hidden sm:table-cell">Job</th>
                    <th className="text-left p-2">Stage</th>
                    <th className="text-left p-2 hidden md:table-cell">Referrer</th>
                    <th className="text-right p-2">Days idle</th>
                  </tr>
                </thead>
                <tbody>
                  {stale.rows.map((r) => (
                    <tr key={r.id} className="border-b border-black/10" data-testid={`stale-row-${r.id}`}>
                      <td className="p-2">
                        <div className="font-semibold">{r.candidate_name}</div>
                        <div className="text-xs text-neutral-500">{r.candidate_email}</div>
                      </td>
                      <td className="p-2 hidden sm:table-cell text-neutral-700">{r.job_title || "—"}</td>
                      <td className="p-2">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border-2 border-black rounded-full bg-brand-yellow">
                          {r.current_stage_name || "—"}
                        </span>
                      </td>
                      <td className="p-2 hidden md:table-cell text-neutral-700">{r.employee_name || "—"}{r.team ? <span className="text-xs text-neutral-500"> · {r.team}</span> : null}</td>
                      <td className="p-2 text-right">
                        <span className={`brutal px-2 py-0.5 text-xs font-black ${r.days_stale >= 30 ? "bg-red-500 text-white" : "bg-brand-yellow"}`}>
                          {r.days_stale}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </BrutalCard>
      )}

      <BrutalCard
        className="p-5 mb-6"
        style={{ background: "linear-gradient(135deg, #FF3AC5 0%, #FF5C00 50%, #FFD600 100%)" }}
        data-testid="yir-card"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-12 h-12 bg-black border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal flex-shrink-0">
            <PartyPopper className="w-6 h-6 text-brand-yellow" strokeWidth={2.6} />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-black/80">Year-in-review</div>
            <h3 className="font-display font-black text-xl leading-tight">Send every employee their personal {(new Date()).getFullYear() - 1} story</h3>
            <p className="text-sm text-black/80 mt-1 leading-relaxed">
              An end-of-year wrap-up email — points earned, referrals made, hires landed, badges unlocked, leaderboard finish. The kind of email people screenshot and share on LinkedIn.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => sendYearInReview(true)} disabled={yirBusy} className="btn-brutal btn-white text-sm" data-testid="yir-preview-btn">
              Preview (dry run)
            </button>
            <button onClick={() => sendYearInReview(false)} disabled={yirBusy} className="btn-brutal bg-black text-white border-black text-sm" data-testid="yir-send-btn">
              Send to all employees
            </button>
          </div>
        </div>
      </BrutalCard>


      {sharing && (
        <BrutalCard className="p-5 mb-6" data-testid="sharing-card">          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-brand-orange text-white border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal">
                <Share2 className="w-6 h-6" strokeWidth={2.4} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Job sharing</div>
                <h3 className="font-display font-black text-xl leading-tight">How your team is spreading the word</h3>
              </div>
            </div>
            <div className="brutal p-1 inline-flex gap-1" data-testid="sharing-period-tabs">
              {[["week","7d"], ["month","This month"], ["all","All time"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setSharingPeriod(val)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${sharingPeriod === val ? "bg-brand-yellow" : "hover:bg-neutral-100"}`}
                  data-testid={`sharing-period-${val}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            <MiniStat label="Total shares" value={sharing.total_shares} color="bg-brand-yellow" testid="share-total" />
            <MiniStat label="Unique sharers" value={sharing.unique_sharers} color="bg-white" testid="share-unique-sharers" />
            <MiniStat label="Jobs shared" value={sharing.unique_jobs_shared} color="bg-white" testid="share-unique-jobs" />
            <MiniStat label="Self-apply results" value={sharing.self_applies} color="bg-brand-green" testid="share-self-applies" />
            <MiniStat label="Conversion" value={`${sharing.conversion_pct}%`} color="bg-brand-orange text-white" testid="share-conversion" sub="shares → applies" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="brutal p-4 bg-white">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">By platform</div>
              {(sharing.by_platform || []).length === 0 ? (
                <p className="text-sm text-neutral-500">No shares yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="share-platforms">
                  {sharing.by_platform.map((p) => (
                    <PlatformRow key={p.platform} platform={p.platform} count={p.count} total={sharing.total_shares} />
                  ))}
                </ul>
              )}
            </div>
            <div className="brutal p-4 bg-white">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Most-shared jobs</div>
              {(sharing.top_jobs || []).length === 0 ? (
                <p className="text-sm text-neutral-500">No shares yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="share-top-jobs">
                  {sharing.top_jobs.map((j) => (
                    <li key={j.job_id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="font-semibold truncate">{j.title || "—"}</span>
                      <span className="brutal bg-brand-yellow px-2 py-0.5 text-xs font-black flex-shrink-0">{j.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="brutal p-4 bg-white">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Top sharers</div>
              {(sharing.top_sharers || []).length === 0 ? (
                <p className="text-sm text-neutral-500">No shares yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="share-top-sharers">
                  {sharing.top_sharers.map((s) => (
                    <li key={s.user_id} className="flex items-center gap-2 text-sm">
                      <img
                        src={s.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(s.name)}`}
                        alt=""
                        className="w-8 h-8 rounded-xl border-2 border-black object-cover bg-white"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate leading-tight">{s.name}</div>
                        {s.team && <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">{s.team}</div>}
                      </div>
                      <span className="brutal bg-brand-yellow px-2 py-0.5 text-xs font-black">{s.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </BrutalCard>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <BrutalCard className="p-6">
          <h3 className="font-display font-black text-xl mb-4">Referrals over last 12 weeks</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#09090B20" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fontWeight: 700 }} stroke="#09090B" />
              <YAxis tick={{ fontSize: 11 }} stroke="#09090B" allowDecimals={false} />
              <Tooltip contentStyle={{ border: "2px solid #09090B", borderRadius: 12, fontWeight: 700 }} />
              <Line type="monotone" dataKey="referrals" stroke="#FF5C00" strokeWidth={3} dot={{ r: 4, fill: "#FF5C00" }} />
            </LineChart>
          </ResponsiveContainer>
        </BrutalCard>

        <BrutalCard className="p-6">
          <h3 className="font-display font-black text-xl mb-4">Pipeline funnel</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#09090B20" />
              <XAxis dataKey="stage" tick={{ fontSize: 10, fontWeight: 700 }} stroke="#09090B" angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} stroke="#09090B" allowDecimals={false} />
              <Tooltip contentStyle={{ border: "2px solid #09090B", borderRadius: 12, fontWeight: 700 }} />
              <Bar dataKey="count" fill="#0047FF" stroke="#09090B" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        </BrutalCard>
      </div>

      <BrutalCard className="p-6">
        <h3 className="font-display font-black text-xl mb-4">Top locations by referrals</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.department_breakdown} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#09090B20" />
            <XAxis type="number" stroke="#09090B" allowDecimals={false} />
            <YAxis type="category" dataKey="department" stroke="#09090B" tick={{ fontWeight: 700, fontSize: 12 }} width={110} />
            <Tooltip contentStyle={{ border: "2px solid #09090B", borderRadius: 12, fontWeight: 700 }} />
            <Bar dataKey="referrals" fill="#FFD600" stroke="#09090B" strokeWidth={2} />
          </BarChart>
        </ResponsiveContainer>
      </BrutalCard>

      {/* Spotlight Engagement */}
      {engagement && (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-brand-pink border-2 border-black rounded-xl flex items-center justify-center shadow-brutal" style={{ background: "linear-gradient(135deg,#FF5C00,#FF3AC5)" }}>
              <Instagram className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="font-display font-black text-3xl">Spotlight Engagement</h2>
              <p className="text-sm text-neutral-700">How often winner cards get shared and downloaded.</p>
            </div>
          </div>

          <div className="bento-grid mb-5">
            <StatCard testid="engagement-total" label="Total events" value={engagement.total_events} color="bg-white" icon={<TrendingUp className="w-6 h-6" />} />
            <StatCard testid="engagement-shares" label="Shares" value={engagement.shares} color="bg-brand-green" icon={<Share2 className="w-6 h-6" />} />
            <StatCard testid="engagement-downloads" label="Downloads" value={engagement.downloads} color="bg-brand-yellow" icon={<Download className="w-6 h-6" />} />
            <StatCard testid="engagement-formats" label="Active formats" value={engagement.by_format.length} color="bg-brand-orange text-white" icon={<Instagram className="w-6 h-6 text-white" />} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <BrutalCard className="p-6">
              <h3 className="font-display font-black text-lg mb-4">By format</h3>
              {engagement.by_format.length === 0 ? (
                <div className="text-neutral-600 text-sm">No share/download events yet. Try sharing from the Spotlight page!</div>
              ) : (
                <div className="space-y-3">
                  {engagement.by_format.map((f) => (
                    <div key={f.format} className="border-2 border-black rounded-xl p-3" data-testid={`format-${f.format}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-display font-black">{f.format}</div>
                        <div className="font-bold text-sm">{f.total} events</div>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <span className="chip bg-brand-green">Shares: {f.share}</span>
                        <span className="chip bg-brand-yellow">Downloads: {f.download}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </BrutalCard>

            <BrutalCard className="p-6">
              <h3 className="font-display font-black text-lg mb-4">Most-shared winners</h3>
              {engagement.top_featured.length === 0 ? (
                <div className="text-neutral-600 text-sm">No winners shared yet.</div>
              ) : (
                <div className="space-y-3">
                  {engagement.top_featured.map((t, i) => (
                    <div key={t.user_id} className="flex items-center gap-3 border-2 border-black rounded-xl p-3">
                      <div className="w-8 h-8 bg-brand-yellow border-2 border-black rounded-lg flex items-center justify-center font-display font-black">{i + 1}</div>
                      <img src={t.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(t.name)}`} alt="" className="w-9 h-9 rounded-full border-2 border-black object-cover" />
                      <div className="flex-1 font-semibold">{t.name}</div>
                      <div className="font-bold">{t.shares} events</div>
                    </div>
                  ))}
                </div>
              )}
            </BrutalCard>
          </div>

          {engagement.timeline.length > 0 && (
            <BrutalCard className="p-6 mt-5">
              <h3 className="font-display font-black text-lg mb-4">Share activity — last 30 days</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={engagement.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#09090B20" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontWeight: 700 }} stroke="#09090B" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#09090B" allowDecimals={false} />
                  <Tooltip contentStyle={{ border: "2px solid #09090B", borderRadius: 12, fontWeight: 700 }} />
                  <Line type="monotone" dataKey="events" stroke="#FF3AC5" strokeWidth={3} dot={{ r: 4, fill: "#FF3AC5" }} />
                </LineChart>
              </ResponsiveContainer>
            </BrutalCard>
          )}
        </div>
      )}
    </div>
  );
}


function MiniStat({ label, value, color = "bg-white", sub, testid }) {
  return (
    <div className={`brutal p-3 ${color}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="font-display font-black text-2xl leading-none mt-1">{value}</div>
      {sub && <div className="text-[9px] uppercase tracking-widest font-semibold opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

const PLATFORM_META = {
  whatsapp: { label: "WhatsApp", color: "#25D366", Icon: MessageCircle },
  linkedin: { label: "LinkedIn", color: "#0A66C2", Icon: Linkedin },
  x:        { label: "X / Twitter", color: "#000000", Icon: Share2 },
  facebook: { label: "Facebook", color: "#1877F2", Icon: Facebook },
  email:    { label: "Email", color: "#FF5C00", Icon: MailIcon },
  copy:     { label: "Copy link", color: "#0047FF", Icon: Link2 },
  native:   { label: "Native share", color: "#09090B", Icon: Share2 },
  qr:       { label: "QR code", color: "#09090B", Icon: QrIcon },
};

function PlatformRow({ platform, count, total }) {
  const meta = PLATFORM_META[platform] || { label: platform, color: "#09090B", Icon: Share2 };
  const Icon = meta.Icon;
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <li className="flex items-center gap-3" data-testid={`platform-row-${platform}`}>
      <div
        className="w-8 h-8 border-2 border-black rounded-xl flex items-center justify-center text-white flex-shrink-0"
        style={{ background: meta.color }}
      >
        <Icon className="w-4 h-4" strokeWidth={2.4} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-semibold text-sm truncate">{meta.label}</span>
          <span className="text-xs font-bold text-neutral-600">{count} · {pct}%</span>
        </div>
        <div className="mt-1 h-2 bg-neutral-100 border-2 border-black rounded-full overflow-hidden">
          <div className="h-full" style={{ width: `${pct}%`, background: meta.color }} />
        </div>
      </div>
    </li>
  );
}
